import { useState, useEffect, useCallback, useRef } from "react";
import { Bluelagoon } from "@bluelagoon";

export default function useCodeScanData(stepId, hasAccess) {
    const [scanResult, setScanResult] = useState(null);
    const [findings, setFindings] = useState([]);
    const [isScanning, setIsScanning] = useState(false);
    const [scanTimedOut, setScanTimedOut] = useState(false);
    const [scanHistory, setScanHistory] = useState([]);
    const pollRef = useRef(null);

    const fetchResults = useCallback(async () => {
        if (!stepId || !hasAccess) return null;
        try {
            const res = await Bluelagoon.getCodeScanResults({ stepId });
            const data = res?.data?.data || res?.data;
            if (data && data.id && data.status) {
                setScanResult(data);
                if (data.status === "COMPLETED" && data.id) {
                    const findingsRes = await Bluelagoon.getCodeScanFindings({ scanId: data.id });
                    const fData = findingsRes?.data?.data || findingsRes?.data;
                    if (fData) setFindings(fData);
                }
                return data.status;
            }
        } catch (e) { /* ignore fetch errors during polling */ }
        return null;
    }, [stepId, hasAccess]);

    const fetchHistory = useCallback(async () => {
        if (!stepId || !hasAccess) return;
        try {
            const res = await Bluelagoon.getCodeScanHistory({ stepId });
            const data = res?.data?.data || res?.data;
            if (Array.isArray(data)) setScanHistory(data);
        } catch (e) { /* ignore */ }
    }, [stepId, hasAccess]);

    const loadScanById = useCallback(async (scanId) => {
        const selected = scanHistory.find((s) => s.id === scanId);
        if (!selected) return;
        setScanResult(selected);
        setScanTimedOut(false);
        setIsScanning(false);
        if (selected.status === "COMPLETED") {
            try {
                const findingsRes = await Bluelagoon.getCodeScanFindings({ scanId });
                const fData = findingsRes?.data?.data || findingsRes?.data;
                if (fData) setFindings(fData);
            } catch (e) { /* ignore */ }
        } else {
            setFindings([]);
        }
    }, [scanHistory]);

    useEffect(() => {
        fetchResults();
        fetchHistory();
    }, [fetchResults, fetchHistory]);

    useEffect(() => {
        const isActive = scanResult && ["PENDING", "RUNNING"].includes(scanResult.status);
        const isRecent = scanResult?.createdAt &&
            (Date.now() - new Date(scanResult.createdAt).getTime()) < 10 * 60 * 1000;

        if (!isActive || !isRecent) {
            if (pollRef.current) clearInterval(pollRef.current);
            if (isActive && !isRecent) {
                setScanTimedOut(true);
                setIsScanning(false);
            } else if (!isActive) {
                setIsScanning(false);
                setScanTimedOut(false);
            }
            return;
        }
        setScanTimedOut(false);
        setIsScanning(true);
        pollRef.current = setInterval(async () => {
            const status = await fetchResults();
            if (status && !["PENDING", "RUNNING"].includes(status)) {
                clearInterval(pollRef.current);
                setIsScanning(false);
                fetchHistory();
            }
        }, 3000);
        return () => clearInterval(pollRef.current);
    }, [scanResult?.status, scanResult?.createdAt, fetchResults, fetchHistory]);

    return {
        scanResult, setScanResult, findings, setFindings,
        isScanning, setIsScanning, scanTimedOut, setScanTimedOut,
        scanHistory, fetchResults, fetchHistory, loadScanById,
    };
}
